import type { HTMLAttributes } from 'react';
import styled from 'styled-components';
import WithStripedShadow from 'components/hoc/WithStripedShadow';
import DisclaimerButton from 'components/pages/placeholders/shared/DisclaimerButton';
import type { TypeImageBannerDetailed, TypeImageBannerFields, TypePositionDetailed } from 'types/autogen/contentful';
import ComponentRenderer from '../../shared/ComponentRenderer';
import type { ExtractSymbol } from '../../shared/types';
import Text from '../Text';

// #region Styles
const Wrapper = styled.div`
    position: relative;
`;

const WrapperWithStripedShadow = styled(WithStripedShadow(Wrapper))`
    display: flex;
`;

const PositionContainer = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
`;
// #endregion Styles

type ImageWrapperProps = {
    id: string;
    dialog?: {
        buttonText: NonNullable<TypeImageBannerDetailed['fields']['buttonText']>;
        quickviewContent: NonNullable<TypeImageBannerDetailed['fields']['quickviewContent']>;
        buttonPosition: NonNullable<TypeImageBannerDetailed['fields']['buttonPosition']>;
    };
    theme?: ExtractSymbol<TypeImageBannerFields['theme']>;
    textOnImage?: TypePositionDetailed;
} & HTMLAttributes<HTMLDivElement>;

function Content(props: Pick<ImageWrapperProps, 'textOnImage' | 'dialog' | 'id' | 'children'>) {
    const { textOnImage, dialog, id, children } = props;
    return (
        <>
            {children}
            {textOnImage && (
                <PositionContainer>
                    <ComponentRenderer {...textOnImage} />
                </PositionContainer>
            )}
            {dialog && (
                <DisclaimerButton
                    disclaimerText={<Text {...dialog.quickviewContent} />}
                    buttonText={dialog.buttonText}
                    quickViewId={id}
                    buttonLocation={dialog.buttonPosition}
                />
            )}
        </>
    );
}

export default function ImageWrapper(props: ImageWrapperProps) {
    const { dialog, textOnImage, id, children, ...restProps } = props;
    const contentProps = { dialog, textOnImage, id, children };

    return (
        <Wrapper {...restProps}>
            <Content {...contentProps} />
        </Wrapper>
    );
}
